import type { AssignableRole, MembershipRole, Role, WorkspaceRole } from "@unbox/db";

// UNB-1912 — the single source of truth for the scoped role model.
//
// Roles are additive: workspace_admin ⊇ merchant_admin ⊇ user. Internal Unbox
// staff (global users.role === "admin") retain full access everywhere.
//
// This module is intentionally pure (types only, no DB / no server deps) so the
// exact same logic runs on the backend (middleware) and the frontend
// (usePermissions), imported via @unbox/api. Do not add runtime imports here.

/** The effective, resolved role for a user within a given brand/workspace scope. */
export type EffectiveRole = "user" | "merchant_admin" | "workspace_admin";

/**
 * A role value for display only. Adds the derived "multiple" state (UNB-2001) for
 * a user whose grants disagree across the scopes being summarised. Never assigned
 * or persisted — it only ever comes out of {@link deriveDisplayRole}.
 */
export type DisplayRole = EffectiveRole | "multiple";

/**
 * Collapse the scoped roles a user holds into a single value to show in a role
 * column: the shared role when they all agree, "multiple" when they diverge.
 * Holding no grants reads as `user`, the default for everyone.
 */
export function deriveDisplayRole(roles: EffectiveRole[]): DisplayRole {
  const [first, ...rest] = roles;
  if (!first) return "user";
  return rest.every((role) => role === first) ? first : "multiple";
}

/**
 * The minimal user shape the permission helpers need. Both the backend
 * `UserWithMerchants` and the frontend `Me` are structurally compatible.
 */
export type PermissionUser = {
  /** Global role — carries internal staff `admin`, not the scoped taxonomy. */
  role: Role;
  merchants: { id: string; role: MembershipRole }[];
  workspaces: { id: string; role: WorkspaceRole; merchants: { id: string }[] }[];
};

/** Resolve the effective role for `user` within `merchantId`. See UNB-1912 matrix. */
export function getEffectiveMerchantRole(user: PermissionUser, merchantId: string): EffectiveRole {
  // Internal Unbox staff bypass — mirrors the existing `role === "admin"` checks.
  if (user.role === "admin") return "workspace_admin";

  // Workspace admin over the workspace that owns this brand → authority over all its brands.
  const isWorkspaceAdmin = user.workspaces.some(
    (w) => w.role === "workspace_admin" && w.merchants.some((m) => m.id === merchantId),
  );
  if (isWorkspaceAdmin) return "workspace_admin";

  // Per-brand merchant admin.
  const membership = user.merchants.find((m) => m.id === merchantId);
  if (membership?.role === "merchant_admin") return "merchant_admin";

  return "user";
}

/** Resolve the effective role for `user` within a whole `workspaceId` (used by invites). */
export function getEffectiveWorkspaceRole(user: PermissionUser, workspaceId: string): EffectiveRole {
  if (user.role === "admin") return "workspace_admin";

  const ws = user.workspaces.find((w) => w.id === workspaceId);
  if (ws?.role === "workspace_admin") return "workspace_admin";

  // Merchant admin of any brand the workspace owns counts as merchant-admin authority here.
  const isMerchantAdmin = user.merchants.some(
    (m) => m.role === "merchant_admin" && (ws?.merchants.some((wm) => wm.id === m.id) ?? false),
  );
  if (isMerchantAdmin) return "merchant_admin";

  return "user";
}

/** Can configure individual brand safety risks & policies. */
export const canConfigureSafety = (role: EffectiveRole): boolean =>
  role === "merchant_admin" || role === "workspace_admin";

/** Can access the workspace area / workspace-level configuration. */
export const canAccessWorkspaceArea = (role: EffectiveRole): boolean => role === "workspace_admin";

// The roles selectable in a given console scope (UNB-2001), independent of who
// is doing the granting. The workspace-level dropdown (create workspace, the
// tree's top level) is a summary + bulk setter: picking Merchant Admin cascades
// that role to every merchant in the workspace, so all three roles are offered.
// Single-brand surfaces (create merchant, add-user-to-merchant, per-merchant
// rows) don't offer the workspace-wide `workspace_admin`. Order here is the
// display order. Combine with `invitableRoles(actor)` when the actor's grant
// authority also matters; grant authority is always re-checked server-side via
// `canAssignRole`.
// Merchant Admin is offered at the workspace level as a bulk setter even though the
// grant itself is per-merchant: selecting it writes a real `merchant_admin` row for
// every merchant the workspace owns. Unlike `workspace_admin` it grants no
// workspace-area access — see `canAccessWorkspaceArea`.
export const workspaceScopeRoles: AssignableRole[] = ["user", "merchant_admin", "workspace_admin"];
export const merchantScopeRoles: AssignableRole[] = ["user", "merchant_admin"];

/** The scoped roles an actor with this effective role may grant to others. */
export function invitableRoles(role: EffectiveRole): AssignableRole[] {
  switch (role) {
    case "workspace_admin":
      return ["user", "merchant_admin", "workspace_admin"];
    case "merchant_admin":
      return ["user", "merchant_admin"];
    default:
      return ["user"];
  }
}

/** Whether `actor` may assign `target` to another team member. */
export const canAssignRole = (actor: EffectiveRole, target: AssignableRole): boolean =>
  invitableRoles(actor).includes(target);
